import type { BusinessActivity, BuyerType, CountryCode, InvoiceInput, LocaleCode, SupplyNature } from "../../../shared/src/index";

const today = (): string => new Date().toISOString().slice(0, 10);

type CountryDraftDefaults = {
  buyerType: BuyerType;
  supplyNature: SupplyNature;
  businessActivity: BusinessActivity;
  taxRegistered: boolean;
  eInvoiceRequested: boolean;
  issueTime?: string;
};

const countryDraftDefaults: Record<CountryCode, CountryDraftDefaults> = {
  SG: {
    buyerType: "business",
    supplyNature: "domestic",
    businessActivity: "professional_services",
    taxRegistered: true,
    eInvoiceRequested: false
  },
  MY: {
    buyerType: "business",
    supplyNature: "domestic",
    businessActivity: "professional_services",
    taxRegistered: false,
    eInvoiceRequested: false,
    issueTime: "09:00"
  },
  ID: {
    buyerType: "business",
    supplyNature: "domestic",
    businessActivity: "goods",
    taxRegistered: true,
    eInvoiceRequested: false
  },
  TH: {
    buyerType: "business",
    supplyNature: "domestic",
    businessActivity: "professional_services",
    taxRegistered: true,
    eInvoiceRequested: false
  }
};

const buyerTypeOrders: Record<CountryCode, BuyerType[]> = {
  SG: ["business", "final_consumer", "individual", "government"],
  MY: ["business", "individual", "government", "final_consumer"],
  ID: ["business", "final_consumer", "government", "individual"],
  TH: ["business", "final_consumer", "government", "individual"]
};

const supplyNatureOrders: Record<CountryCode, SupplyNature[]> = {
  SG: ["domestic", "export_services", "export_goods", "exempt", "reverse_charge"],
  MY: ["domestic", "export_services", "export_goods", "reverse_charge", "exempt"],
  ID: ["domestic", "export_goods", "export_services", "exempt", "reverse_charge"],
  TH: ["domestic", "export_goods", "export_services", "exempt", "reverse_charge"]
};

const businessActivityOrders: Record<CountryCode, BusinessActivity[]> = {
  SG: [
    "professional_services",
    "general_services",
    "it_services",
    "advertising_services",
    "logistics",
    "goods",
    "retail",
    "food_and_beverage",
    "luxury_goods",
    "other"
  ],
  MY: [
    "professional_services",
    "it_services",
    "food_and_beverage",
    "logistics",
    "goods",
    "retail",
    "general_services",
    "advertising_services",
    "luxury_goods",
    "other"
  ],
  ID: [
    "goods",
    "luxury_goods",
    "retail",
    "professional_services",
    "general_services",
    "logistics",
    "it_services",
    "advertising_services",
    "food_and_beverage",
    "other"
  ],
  TH: [
    "professional_services",
    "general_services",
    "goods",
    "retail",
    "it_services",
    "logistics",
    "food_and_beverage",
    "advertising_services",
    "luxury_goods",
    "other"
  ]
};

export const getCountryDraftDefaults = (country: CountryCode): CountryDraftDefaults => countryDraftDefaults[country];

export const getCountryBuyerTypeOptions = (country: CountryCode): BuyerType[] => buyerTypeOrders[country];

export const getCountrySupplyNatureOptions = (country: CountryCode): SupplyNature[] => supplyNatureOrders[country];

export const getCountryBusinessActivityOptions = (country: CountryCode): BusinessActivity[] => businessActivityOrders[country];

export const getCountryDefaultRate = (
  country: CountryCode,
  taxRegistered: boolean,
  supplyNature: SupplyNature,
  businessActivity: BusinessActivity
): number => {
  if (!taxRegistered || supplyNature === "export_services" || supplyNature === "export_goods" || supplyNature === "exempt") {
    return 0;
  }
  if (country === "SG") {
    return 9;
  }
  if (country === "MY") {
    return businessActivity === "food_and_beverage" || businessActivity === "logistics" ? 6 : 8;
  }
  if (country === "TH") {
    return 7;
  }
  return businessActivity === "luxury_goods" ? 12 : 11;
};

export const makeDraft = (country: CountryCode, locale: LocaleCode): InvoiceInput => {
  const defaults = getCountryDraftDefaults(country);
  const currency = country === "SG" ? "SGD" : country === "MY" ? "MYR" : country === "TH" ? "THB" : "IDR";
  const taxRegistered = defaults.taxRegistered;
  const supplyNature = defaults.supplyNature;
  const businessActivity = defaults.businessActivity;
  const taxRate = getCountryDefaultRate(country, taxRegistered, supplyNature, businessActivity);

  return {
    country,
    locale,
    issueDate: today(),
    issueTime: defaults.issueTime,
    invoiceType: country === "TH" ? "tax_invoice" : undefined,
    invoiceNumber: `${country}-${today().replaceAll("-", "")}-001`,
    seller: {
      legalName: "",
      registrationNumber: "",
      taxNumber: "",
      address: {
        line1: "",
        city: "",
        postalCode: "",
        countryCode: country
      },
      phone: "",
      email: ""
    },
    buyer: {
      legalName: "",
      taxNumber: "",
      address: {
        line1: "",
        city: "",
        postalCode: "",
        countryCode: country
      },
      phone: "",
      email: ""
    },
    buyerType: defaults.buyerType,
    taxRegistered,
    eInvoiceRequested: defaults.eInvoiceRequested,
    supplyNature,
    businessActivity,
    notes: [],
    turnoverLast12Months: 0,
    projectedTurnoverNext12Months: 0,
    items: [
      {
        description: "",
        classificationCode: country === "MY" ? "004" : country === "ID" ? "GEN" : undefined,
        quantity: 1,
        unitPrice: 0,
        amountExcludingTax: 0,
        taxRate,
        taxAmount: 0,
        totalAmount: 0
      }
    ],
    totals: {
      currency,
      subtotal: 0,
      taxTotal: 0,
      grandTotal: 0,
      roundingTotal: 0,
      prepaidTotal: 0
    }
  };
};

export const recalculateDraft = (draft: InvoiceInput): InvoiceInput => {
  const taxRate = getCountryDefaultRate(
    draft.country,
    Boolean(draft.taxRegistered),
    draft.supplyNature ?? "domestic",
    draft.businessActivity ?? "general_services"
  );

  const items = draft.items.map((item) => {
    const quantity = Number(item.quantity ?? 0);
    const unitPrice = Number(item.unitPrice ?? 0);
    const amountExcludingTax = Number(item.amountExcludingTax ?? quantity * unitPrice);
    const lineTaxRate = Number(item.taxRate ?? taxRate);
    const taxAmount = Number(item.taxAmount ?? (amountExcludingTax * lineTaxRate) / 100);
    const totalAmount = Number(item.totalAmount ?? amountExcludingTax + taxAmount);
    return {
      ...item,
      amountExcludingTax,
      taxRate: lineTaxRate,
      taxAmount,
      totalAmount
    };
  });

  const subtotal = items.reduce((sum, item) => sum + Number(item.amountExcludingTax ?? 0), 0);
  const taxTotal = items.reduce((sum, item) => sum + Number(item.taxAmount ?? 0), 0);
  const grandTotal = subtotal + taxTotal + Number(draft.totals.roundingTotal ?? 0) - Number(draft.totals.prepaidTotal ?? 0);

  return {
    ...draft,
    items,
    totals: {
      ...draft.totals,
      subtotal,
      taxTotal,
      grandTotal
    }
  };
};

export const mergeExtractedDraft = (draft: InvoiceInput, extracted: Partial<InvoiceInput>): InvoiceInput => {
  return recalculateDraft({
    ...draft,
    ...extracted,
    seller: {
      ...draft.seller,
      ...(extracted.seller ?? {}),
      address: {
        ...(draft.seller.address ?? {}),
        ...((extracted.seller?.address as object | undefined) ?? {})
      }
    },
    buyer: {
      ...draft.buyer,
      ...(extracted.buyer ?? {}),
      address: {
        ...(draft.buyer.address ?? {}),
        ...((extracted.buyer?.address as object | undefined) ?? {})
      }
    },
    totals: {
      ...draft.totals,
      ...(extracted.totals ?? {})
    },
    items: extracted.items ?? draft.items
  });
};
